import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { CustomProperties } from '@innovation-os-ng/core/core';
import { CompoundFilter, Filter, IqlQueryOperator, IqlSortOperator, LeafFilter } from '@itonics/filters';
import { TranslateService } from '@itonics/ngx-translate-simple';
import { take } from 'rxjs';
import { FactActionEvent } from '../../facts/interfaces/fact';
import { UserFeedbackBarComponent } from '../component/user-feedback-bar/user-feedback-bar.component';
import { Evaluation, EvaluationQuery } from '../interfaces/evaluation';
import { Document, FeedDocument, FeedEvaluationBasis, SearchTip, SignalFilterOption, SortOption } from '../interfaces/feed';
import { EvaluationService } from '../services/evaluation.service';

@Component({
  selector: 'signals-feeds-base',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush
})
/* eslint-disable @angular-eslint/component-class-suffix */
export class BaseFeedList implements OnChanges {
  @Input()
  filter: Filter;

  @Input()
  public source: string;

  @Input()
  public searchQuery: string;

  @Input()
  public signalUris: Array<string>;

  @Input()
  public feedType: Array<string>;

  @Input()
  public sortOption: SortOption;

  @Input()
  public evaluationBasis: FeedEvaluationBasis;

  @Input()
  public searchTips: Array<SearchTip>;

  // Output events
  @Input()
  public onFeedLoaded: EventEmitter<{ totalFeeds: number }>;

  @Input()
  public feedPartClicked: EventEmitter<FactActionEvent>;

  @Input()
  public feedLoadingStatus: EventEmitter<{ isLoadingFeeds: boolean }>;

  @Input()
  public selectedFilterOption: SignalFilterOption;

  /**
   * Emit event name for tracking via ancestor / hosting app
   */
  @Output()
  public trackEvent: EventEmitter<string | { eventName: string; customProperties?: CustomProperties }> = new EventEmitter<
    string | { eventName: string; customProperties?: CustomProperties }
  >();

  // This user's evaluations of the signals
  public userEvaluations: Array<Evaluation> = [];

  public feeds: Array<FeedDocument | UserFeedbackBarComponent> = [];

  // More relevant tips to display when the feed is empty
  // based on the current SelectedFilterOption
  public filterSpecificSearchTips: Array<SearchTip> = null;

  // How many signals are fetched in each 'page'
  public limit: number = 20;
  public listenToScroll: boolean;

  // Duration (in ms) of the CSS transition when we're removing a signal from the feed
  public transitionDuration: number = 500;
  @HostBinding('style.--transition-timing')
  public transitionTiming: string = `${this.transitionDuration}ms`;

  constructor(
    protected readonly cdr: ChangeDetectorRef,
    protected readonly evaluationService: EvaluationService,
    protected readonly translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedFilterOption) {
      this.updateEmptyResultsTips();
    }
  }

  /**
   * Update the tips that will show for an empty results list
   * based on the currently selected filter option
   */
  protected updateEmptyResultsTips() {
    const tipsKeys = this.selectedFilterOption?.emptyResultsTips;
    this.filterSpecificSearchTips = tipsKeys?.map((key) => ({
      tip: this.translate.instant(key)
    }));
  }

  protected buildFilterQuery(forAggregation?: boolean, aggregateBy = []): CompoundFilter {
    const compoundFilter: CompoundFilter = new CompoundFilter(IqlQueryOperator.AND);

    const sourceOnAggregate = aggregateBy.find((by) => by === 'source');

    if (this.feedType && this.feedType.length && (!forAggregation || sourceOnAggregate)) {
      compoundFilter.addFilter(new LeafFilter('type').setValues(this.feedType));
    }

    if (this.searchQuery) {
      compoundFilter.addFilter(new LeafFilter('*').setSearch(this.searchQuery));
    }

    if (this.source && !forAggregation) {
      compoundFilter.addFilter(new LeafFilter('source').setValues([this.source]));
    }
    if (this.filter) {
      compoundFilter.addFilter(this.filter);
    }
    if (this.signalUris) {
      compoundFilter.addFilter(new LeafFilter('uri').setValues(this.signalUris));
    }
    if (this.sortOption) {
      compoundFilter.setSort([
        {
          by: this.sortOption.sortBy,
          direction: this.sortOption.sortOrder as IqlSortOperator
        }
      ]);
    }

    return compoundFilter;
  }

  /**
   * This method is used to emit loading status
   *
   * @param isLoadingFeeds : boolean
   */
  protected emitFeedLoadingStatus(isLoadingFeeds: boolean): void {
    if (this.feedLoadingStatus) {
      this.feedLoadingStatus.emit({ isLoadingFeeds });
    }
  }

  /**
   * Load this user's evaluations of the given signals
   */
  protected loadFeedEvaluations(feeds: Array<Document | UserFeedbackBarComponent> = this.feeds || [], replaceOld: boolean = true) {
    // Build list of signals to fetch
    const evaluationQuery: EvaluationQuery = {
      documentUris: feeds.map((feed) => encodeURIComponent(feed.uri)).join(','),
      limit: feeds.length * 5 // A document can have <1 evaluations, so increase the limit to ensure we get all of them
    };

    // Build the query (if the selected filter option is specific to one query / search field)
    if (this.selectedFilterOption?.includeEvaluationBasis) {
      if (this.evaluationBasis) {
        evaluationQuery[this.evaluationBasis.evaluationBasis] = this.evaluationBasis.uri;
      } else {
        evaluationQuery.searchQueryHash = this.evaluationService.generateUrlFriendlyHash(this.filter);
      }
    }

    // Fetch evaluations and map to this.userEvaluations
    this.evaluationService
      .get(evaluationQuery)
      .pipe(take(1))
      .subscribe((evaluations) => {
        if (replaceOld) {
          this.userEvaluations = evaluations.items;
        } else {
          // Combine and dedup user evaluations (only dedup evalutations that have a uri)
          const userEvaluationUris = {};
          this.userEvaluations = [...this.userEvaluations, ...evaluations.items].filter((evaluation) => {
            if (userEvaluationUris[evaluation.uri]) {
              return false;
            }
            if (evaluation.uri) {
              userEvaluationUris[evaluation.uri] = true;
            }
            return true;
          });
        }
        this.detectChanges();
      });
  }

  protected detectChanges() {
    this.cdr.detectChanges();
  }

  /**
   * Stub, this should be replaced in the component that extends this one
   */
  public scrollToEnd() {}

  /**
   * Remove the given signal from the signals feed
   * First, we add a class to trigger the CSS transitions
   * Next, once the transitions are done, we remove the signal from the feeds array
   *
   * @param signal Signal to hide (should be an element of the this.feeds array)
   */
  public hideSignal(signal: FeedDocument) {
    // We only want to hide signals in certain filter options
    if (!this.selectedFilterOption?.showBlockedSignalAnimation) {
      return;
    }
    // Trigger the CSS transitions
    signal.removedByUser = true;

    setTimeout(() => {
      // Remove the signal from the array of feeds
      const signalPosition = this.feeds.findIndex((element) => element === signal);
      this.feeds.splice(signalPosition, 1);

      // If the number of signals drops too far, load more signals
      if (this.feeds.length < this.limit && this.listenToScroll) {
        this.scrollToEnd();
      }
    }, this.transitionDuration);
  }
}
